import type {
  DocumentRegisterEntry,
  DocumentRevision,
  OrderSummary,
  PublicUser,
  ReportType,
  Resource,
  ResourceCategory,
  SubmissionSummary,
} from "@shared/types";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: {
      ...(init?.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    const data = await res.json();
    if (!res.ok) {
      const msg =
        typeof data?.error === "string"
          ? data.error
          : data?.error
            ? JSON.stringify(data.error)
            : res.statusText;
      throw new Error(msg || "Request failed");
    }
    return data as T;
  }

  if (!res.ok) throw new Error(res.statusText || "Request failed");
  return undefined as T;
}

export const api = {
  health: () => request<{ ok: boolean }>("/api/health"),
  config: () =>
    request<{
      appName: string;
      legalEntity: string;
      urbanDevelopmentPerLotCents: number;
      stripeConfigured: boolean;
    }>("/api/config"),

  me: () => request<{ user: PublicUser }>("/api/auth/me"),
  register: (body: {
    email: string;
    password?: string;
    fullName: string;
    companyName?: string;
    phone?: string;
  }) =>
    request<{ user: PublicUser; magicLinkDev?: string }>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  login: (body: { email: string; password: string }) =>
    request<{ user: PublicUser }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  magicLink: (email: string) =>
    request<{ ok: boolean; magicLinkDev?: string; message: string }>("/api/auth/magic-link", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  magicConsume: (token: string) =>
    request<{ user: PublicUser }>("/api/auth/magic-consume", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
  logout: () => request<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  updateMe: (body: Record<string, unknown>) =>
    request<{ user: PublicUser }>("/api/auth/me", {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  categories: () => request<{ categories: ResourceCategory[] }>("/api/resources/categories"),
  resources: (params?: { category?: string; q?: string }) => {
    const sp = new URLSearchParams();
    if (params?.category) sp.set("category", params.category);
    if (params?.q) sp.set("q", params.q);
    const q = sp.toString();
    return request<{ resources: Resource[] }>(`/api/resources${q ? `?${q}` : ""}`);
  },
  resource: (slug: string) => request<{ resource: Resource }>(`/api/resources/${slug}`),
  purchaseResource: (id: string) =>
    request<{
      ok?: boolean;
      free?: boolean;
      alreadyOwned?: boolean;
      checkoutUrl?: string;
      mock?: boolean;
      submissionId?: string | null;
    }>(`/api/resources/${id}/purchase`, { method: "POST" }),
  downloadResource: async (id: string, fileName?: string) => {
    const res = await fetch(`/api/resources/${id}/download`, { credentials: "include" });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { error?: string }).error || "Download failed");
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "download.pdf";
    a.click();
    URL.revokeObjectURL(url);
  },

  reportTypes: () => request<{ reportTypes: ReportType[] }>("/api/submissions/report-types"),
  reportType: (slug: string) =>
    request<{ reportType: ReportType }>(`/api/submissions/report-types/${slug}`),
  submissions: () => request<{ submissions: SubmissionSummary[] }>("/api/submissions"),
  createSubmission: (body: { reportTypeId: string; projectName?: string }) =>
    request<{ id: string }>("/api/submissions", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  submission: (id: string) => request<{ submission: any }>(`/api/submissions/${id}`),
  updateSubmission: (id: string, body: { projectName?: string; answers?: Record<string, unknown> }) =>
    request<{ ok: boolean }>(`/api/submissions/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  uploadSubmissionFile: async (id: string, file: File, kind = "client_upload") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    return request<{ id: string; fileName: string }>(`/api/submissions/${id}/files`, {
      method: "POST",
      body: fd,
    });
  },
  checkoutSubmission: (id: string) =>
    request<{ checkoutUrl: string; mock?: boolean }>(`/api/submissions/${id}/checkout`, {
      method: "POST",
    }),
  submitSubmission: (id: string) =>
    request<{ ok: boolean; status: string }>(`/api/submissions/${id}/submit`, {
      method: "POST",
    }),
  setSubmissionStatus: (
    id: string,
    body: { status: string; reviewerNotes?: string; draftContent?: string }
  ) =>
    request<{ ok: boolean }>(`/api/submissions/${id}/status`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  generatePdf: (id: string) =>
    request<{ ok: boolean; fileName: string }>(`/api/submissions/${id}/generate-pdf`, {
      method: "POST",
    }),
  downloadSubmission: async (id: string, fileName?: string) => {
    const res = await fetch(`/api/submissions/${id}/download`, { credentials: "include" });
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "report.html";
    a.click();
    URL.revokeObjectURL(url);
  },

  orders: () => request<{ orders: OrderSummary[] }>("/api/payments/history"),
  paymentConfig: () =>
    request<{ mockPayments: boolean; publishableKey: string | null }>("/api/payments/config"),

  enquiry: (body: Record<string, string>) =>
    request<{ ok: boolean; id: string }>("/api/enquiries", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  adminAnalytics: () => request<Record<string, unknown>>("/api/admin/analytics"),
  adminResources: () => request<{ resources: any[] }>("/api/admin/resources"),
  adminClients: () => request<{ clients: any[] }>("/api/admin/clients"),
  adminEnquiries: () => request<{ enquiries: any[] }>("/api/enquiries"),
  createResource: (body: Record<string, unknown>) =>
    request<{ id: string }>("/api/resources", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateResource: (id: string, body: Record<string, unknown>) =>
    request<{ ok: boolean }>(`/api/resources/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  uploadResourceFile: async (id: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return request<{ ok: boolean }>(`/api/resources/${id}/upload`, {
      method: "POST",
      body: fd,
    });
  },

  /** Internal IMS document / template version control register (staff only). */
  documentRegister: (params?: { status?: string; kind?: string; q?: string }) => {
    const sp = new URLSearchParams();
    if (params?.status) sp.set("status", params.status);
    if (params?.kind) sp.set("kind", params.kind);
    if (params?.q) sp.set("q", params.q);
    const q = sp.toString();
    return request<{ documents: DocumentRegisterEntry[] }>(
      `/api/admin/document-register${q ? `?${q}` : ""}`
    );
  },
  documentRegisterEntry: (id: string) =>
    request<{ document: DocumentRegisterEntry; revisions: DocumentRevision[] }>(
      `/api/admin/document-register/${id}`
    ),
  createDocumentRegister: (body: Record<string, unknown>) =>
    request<{ id: string; docId: string; revisionId: string | null }>(
      "/api/admin/document-register",
      { method: "POST", body: JSON.stringify(body) }
    ),
  updateDocumentRegister: (id: string, body: Record<string, unknown>) =>
    request<{ ok: boolean }>(`/api/admin/document-register/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  addDocumentRevision: (id: string, body: Record<string, unknown>) =>
    request<{ id: string }>(`/api/admin/document-register/${id}/revisions`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  issueDocumentRevision: (id: string, revId: string) =>
    request<{ ok: boolean; currentRevisionId: string }>(
      `/api/admin/document-register/${id}/revisions/${revId}/issue`,
      { method: "POST" }
    ),
  exportDocumentRegister: () =>
    request<{
      exportedAt: string;
      brand: string;
      imsNote: string;
      documents: unknown[];
      revisions: unknown[];
    }>("/api/admin/document-register/export"),
};
